// Rechnungs-Hooks. RLS:
// - kunde sieht eigene
// - buero verwaltet alle

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type InvoiceStatus = "draft" | "open" | "partly_paid" | "paid" | "overdue" | "cancelled";

export interface Invoice {
  id: string;
  invoice_number: string;
  customer_id: string;
  period_start: string | null;
  period_end: string | null;
  status: InvoiceStatus;
  subtotal_cents: number;
  vat_cents: number;
  total_cents: number;
  paid_cents: number;
  due_at: string;
  pdf_url: string | null;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceLine {
  id: string;
  invoice_id: string;
  order_id: string | null;
  description: string;
  qty: number;
  unit_price_cents: number;
  vat_rate: number;
  line_total_cents: number;
}

export interface InvoiceWithJoins extends Invoice {
  customer: { display_name: string; email: string | null; type: "privat" | "gewerblich" } | null;
  lines: InvoiceLine[];
}

const SELECT = `*, customer:customers(display_name, email, type), lines:invoice_lines(*)`;

export function useInvoices(filter: { status?: InvoiceStatus; customerId?: string } = {}) {
  return useQuery({
    queryKey: ["invoices", filter],
    queryFn: async () => {
      let q = supabase.from("invoices").select(SELECT).order("created_at", { ascending: false });
      if (filter.status) q = q.eq("status", filter.status);
      if (filter.customerId) q = q.eq("customer_id", filter.customerId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as InvoiceWithJoins[];
    },
  });
}

export function useInvoice(id?: string) {
  return useQuery({
    queryKey: ["invoice", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase.from("invoices").select(SELECT).eq("id", id!).single();
      if (error) throw error;
      return data as InvoiceWithJoins;
    },
  });
}

export function useMyInvoices() {
  return useQuery({
    queryKey: ["my-invoices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*, lines:invoice_lines(*)")
        .order("due_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as InvoiceWithJoins[];
    },
  });
}

export function useUpdateInvoiceStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, paidCents }: { id: string; status: InvoiceStatus; paidCents?: number }) => {
      const upd: Partial<Invoice> = { status };
      if (status === "paid") {
        upd.paid_at = new Date().toISOString();
      }
      if (paidCents != null) upd.paid_cents = paidCents;
      const { error } = await supabase.from("invoices").update(upd).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export const STATUS_INVOICE_LABEL: Record<InvoiceStatus, string> = {
  draft: "Entwurf",
  open: "Offen",
  partly_paid: "Teilbezahlt",
  paid: "Bezahlt",
  overdue: "Überfällig",
  cancelled: "Storniert",
};

export const STATUS_INVOICE_PILL: Record<InvoiceStatus, string> = {
  draft: "pill bg-hof-100 text-hof-800",
  open: "pill-info",
  partly_paid: "pill bg-sonne-100 text-sonne-800",
  paid: "pill-success",
  overdue: "pill-danger",
  cancelled: "pill bg-hof-200 text-hof-700",
};
